import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

const WorkflowPage: React.FC = () => {
  const navigate = useNavigate();

  const workflowSteps = [
    { stage: 1, name: 'TOR Analysis', icon: 'pi pi-file-edit', color: '#3B82F6' },
    { stage: 2, name: 'Indicator Extraction', icon: 'pi pi-list', color: '#22C55E' },
    { stage: 3, name: 'Questionnaire Design', icon: 'pi pi-clipboard', color: '#F97316' },
    { stage: 4, name: 'KoboToolbox Deployment', icon: 'pi pi-cloud-upload', color: '#A855F7' },
    { stage: 5, name: 'Pilot Testing', icon: 'pi pi-check-circle', color: '#06B6D4' },
    { stage: 6, name: 'Sampling Strategy', icon: 'pi pi-users', color: '#EF4444' },
    { stage: 7, name: 'Data Collection', icon: 'pi pi-database', color: '#EC4899' },
    { stage: 8, name: 'Statistical Analysis', icon: 'pi pi-chart-bar', color: '#8B5CF6' },
    { stage: 9, name: 'Report Generation', icon: 'pi pi-file-pdf', color: '#14B8A6' },
    { stage: 10, name: 'Policy Recommendations', icon: 'pi pi-lightbulb', color: '#F59E0B' }
  ];

  const agentDetails = [
    { step: 1, name: 'Indicator Extraction Agent', description: 'Parses TORs and extracts measurable research indicators.', inputs: ['TOR Document', 'Study Context'], outputs: ['Indicator Table', 'Objectives'], duration: '2-5 min', color: '#3B82F6', icon: 'pi pi-file-edit' },
    { step: 2, name: 'Questionnaire Design Agent', description: 'Transforms indicators into structured questionnaire items.', inputs: ['Indicator List', 'Survey Type'], outputs: ['Questionnaire JSON', 'Skip Logic'], duration: '3-7 min', color: '#22C55E', icon: 'pi pi-list' },
    { step: 3, name: 'KoboToolbox Integration Agent', description: 'Converts questionnaire to XLSForm and deploys.', inputs: ['Questionnaire JSON', 'Credentials'], outputs: ['Form URL', 'Validation Report'], duration: '1-3 min', color: '#F97316', icon: 'pi pi-cloud-upload' },
    { step: 4, name: 'Real-time Monitoring Agent', description: 'Monitors data collection progress and quality.', inputs: ['Data Stream', 'Thresholds'], outputs: ['Dashboard', 'Alerts'], duration: 'Continuous', color: '#A855F7', icon: 'pi pi-eye' },
    { step: 5, name: 'Data Cleaning Agent', description: 'Processes and cleans collected data.', inputs: ['Raw Data', 'Rules'], outputs: ['Clean Dataset', 'Report'], duration: '15-30 min', color: '#06B6D4', icon: 'pi pi-wrench' },
    { step: 6, name: 'Statistical Analysis Agent', description: 'Performs comprehensive statistical analysis.', inputs: ['Clean Dataset', 'Specs'], outputs: ['Results', 'Visualizations'], duration: '20-45 min', color: '#EF4444', icon: 'pi pi-chart-bar' }
  ];

  const timelinePhases = [
    { phase: 1, name: 'Setup Phase', description: 'TOR to deployment', time: '15-30 min', traditional: '2-4 weeks', color: '#22C55E' },
    { phase: 2, name: 'Collection Phase', description: 'Automated monitoring', time: '2-8 weeks', traditional: 'continuous', color: '#3B82F6' },
    { phase: 3, name: 'Analysis Phase', description: 'Cleaning to recommendations', time: '2-4 hours', traditional: '4-8 weeks', color: '#A855F7' }
  ];

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };

  return (
    <div>
      {/* Hero */}
      <div style={{ padding: '3rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem 0', color: '#1f2937' }}>10-Stage AI Agent Workflow</h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: '700px', margin: '0 auto 2rem auto' }}>
          Our coordinated AI agents automate your entire research lifecycle.
        </p>
        <Button label="Back to Platform" icon="pi pi-arrow-left" className="p-button-outlined" onClick={() => navigate('/platform')} />
      </div>

      {/* Workflow Steps */}
      <div style={{ padding: '3rem 2rem', backgroundColor: 'white' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Complete Automation Pipeline</h2>
          <p style={{ color: '#6b7280' }}>Each stage handled by a specialized AI agent</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
          {workflowSteps.slice(0, 5).map((step, i) => (
            <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: step.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem auto' }}>
                <i className={step.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i>
              </div>
              <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.25rem 0', color: '#1f2937' }}>{step.name}</h3>
              <span style={{ fontSize: '0.75rem', color: '#3B82F6', fontWeight: 500 }}>Stage {step.stage}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
          {workflowSteps.slice(5).map((step, i) => (
            <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: step.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem auto' }}>
                <i className={step.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i>
              </div>
              <h3 style={{ fontSize: '0.875rem', margin: '0 0 0.25rem 0', color: '#1f2937' }}>{step.name}</h3>
              <span style={{ fontSize: '0.75rem', color: '#3B82F6', fontWeight: 500 }}>Stage {step.stage}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Agent Details */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Agent Details</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          {agentDetails.map((agent, i) => (
            <div key={i} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '8px', backgroundColor: agent.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={agent.icon} style={{ color: 'white' }}></i>
                </div>
                <div>
                  <h3 style={{ fontSize: '0.875rem', margin: 0, color: '#1f2937' }}>{agent.name}</h3>
                  <span style={{ fontSize: '0.75rem', color: agent.color }}>Stage {agent.step} • {agent.duration}</span>
                </div>
              </div>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.75rem 0' }}>{agent.description}</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.75rem' }}>
                <div>
                  <span style={{ color: '#9ca3af' }}>Inputs:</span>
                  {agent.inputs.map((inp, j) => <div key={j} style={{ color: '#6b7280' }}>• {inp}</div>)}
                </div>
                <div>
                  <span style={{ color: '#9ca3af' }}>Outputs:</span>
                  {agent.outputs.map((out, j) => <div key={j} style={{ color: '#22C55E' }}>✓ {out}</div>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div style={{ padding: '3rem 2rem', backgroundColor: 'white' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Project Timeline</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
          {timelinePhases.map((phase, i) => (
            <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: phase.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto' }}>
                <span style={{ color: 'white', fontWeight: 'bold' }}>{phase.phase}</span>
              </div>
              <h3 style={{ fontSize: '1.125rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{phase.name}</h3>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>{phase.description}</p>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: phase.color }}>{phase.time}</div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>vs. {phase.traditional}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'inline-block', backgroundColor: '#3B82F6', color: 'white', borderRadius: '16px', padding: '1.5rem 3rem' }}>
            <h3 style={{ margin: '0 0 0.5rem 0' }}>Total Time Savings</h3>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>90%</div>
            <p style={{ margin: 0, color: '#bfdbfe' }}>From months to weeks</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#3B82F6', textAlign: 'center' }}>
        <h2 style={{ color: 'white', fontSize: '2rem', margin: '0 0 1rem 0' }}>Ready to Experience the Workflow?</h2>
        <p style={{ color: '#bfdbfe', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
          See our AI agents in action with a personalized demo.
        </p>
        <Button label="Contact Us" icon="pi pi-envelope" className="p-button-lg" style={{ backgroundColor: 'white', color: '#3B82F6', border: 'none', marginRight: '1rem' }} onClick={() => navigate('/contact')} />
        <Button label="View Platform" icon="pi pi-arrow-right" className="p-button-outlined p-button-lg" style={{ borderColor: 'white', color: 'white' }} onClick={() => navigate('/platform')} />
      </div>
    </div>
  );
};

export default WorkflowPage;
